import {DrawerRecommendation_BrandsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/recommendations.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useBrandsRoute} from '@aboutyou/router';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  BrandLogo,
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Heading20,
  Icon,
  Text,
  UnstyledLinkLinaria,
} from '@aboutyou/ui-components';
import {addParamsToUrl} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';

const transparentParam = new URLSearchParams();
transparentParam.set('brightness', '0.95');
transparentParam.set('trim', '1');

interface NavigationTopBrandsProps {
  brands: DrawerRecommendation_BrandsSection;
  isVisible: boolean;
}

export const NavigationTopBrands: React.FC<NavigationTopBrandsProps> = ({
  brands,
  isVisible,
}) => {
  const {t} = useTranslation();
  const trackOriginClick = useTrackOriginClick();
  const brandsRoute = useBrandsRoute();
  return (
    <BrandContainer data-testid="brandsContainer">
      <Text as="div" size="px12" color="Grey1">
        {t('Brands')}
      </Text>
      <Headline as={Headline}>{t('Our top brands')}</Headline>
      <AllBrands
        data-testid="brandListItemAllBrands"
        onClick={trackOriginClick('top_brands_reco')}
        to={brandsRoute}
        aria-label={undefined}
      >
        {t('All brands')}
        <ChevronIcon size="XSS" asset={GenericIconName.ArrowRight} />
      </AllBrands>

      <BrandTiles data-testid="topBrandsGrid">
        {brands?.brands.map(({brand, path}, index) => {
          const brandContent = brand.blackLogoImage?.url ? (
            <BrandIcon
              image={{
                ...brand.blackLogoImage,
                url: addParamsToUrl(brand.blackLogoImage.url, transparentParam),
              }}
              imageWidths={[56, 112, 224, 448]}
              maxHeight="100%"
              maxWidth="100%"
              name={brand.name}
              onClick={trackOriginClick('brands_logo')}
              openExternalLinkInNewTab={false}
              path={path.url}
            />
          ) : (
            brand.name
          );

          const brandTileProps = {
            'data-testid': `topBrandItem_${index}`,
            key: path.url,
          };

          // only track when the drawer is actually visible - do NOT track when it is unvisible or sliding out
          return isVisible ? (
            <TrackPromotions
              {...brandTileProps}
              Comp={BrandTile}
              onClickTracked={trackOriginClick('top_brands_reco')}
              promotion={{brand, source: TrackingPromotionCreative.BrandReco}}
              streamPosition={index}
            >
              {brandContent}
            </TrackPromotions>
          ) : (
            <BrandTile {...brandTileProps}>{brandContent}</BrandTile>
          );
        })}
      </BrandTiles>
    </BrandContainer>
  );
};

const BrandContainer = styled.section`
  padding: 40px 0;
  text-align: center;
`;

const Headline = styled(Heading20)`
  font-weight: 900;
  padding: 4px 0;
`;

const AllBrands = styled(UnstyledLinkLinaria)`
  align-items: center;
  display: flex;
  justify-content: center;
  padding-bottom: 6px;
  padding-top: 25px;
  font-size: ${FONT_SIZES.px14};
`;

const BrandTiles = styled.menu`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 4px;
  padding: 0;
  margin-bottom: 10px;
`;

const BrandTile = styled.li`
  aspect-ratio: 16 / 7;
  background-color: ${COLORS.Grey4};
  display: block;
  position: relative;
`;

const BrandIcon = styled(BrandLogo)`
  box-sizing: border-box;
  display: block;
  height: 100%;
  padding: 24px;
  width: 100%;
`;

const ChevronIcon = styled(Icon)`
  margin-left: 5px;
  margin-top: 2px;
`;
